import { useMemo } from 'react';
import useTeamSettings from '../../containers/team/hooks/useTeamSettingsService';
import useHasPermission from '../../access-control/hooks/useHasPermission';
import { RbacPermissions } from '../../core/interfaces/user';
import { EnvConfig } from '../../core/env-config';
import { useConnectivityAccess } from '../../containers/team/Connectivity/hooks/useConnectivityAccess';
import { formatDataIdAttr } from '../../core/utils/dataIdFormatter';
import {
  SettingsNavItem,
  SettingsSection,
  SettingsNavItemConfig,
  SettingsNavSection,
  SettingsNavVisibilityContext
} from '../interfaces/settingsNav';
import {
  SETTINGS_NAV,
  SETTINGS_SECTION_ORDER,
  SETTINGS_SECTION_TITLES
} from '../constants/settingsNav';
import { useBillingNav } from '../../containers/payment/billing-nav/BillingNavContext';
import { BillingTabDescriptor } from '../../containers/payment/billing-nav/interfaces';

function resolveItems(
  configs: SettingsNavItemConfig[],
  ctx: SettingsNavVisibilityContext
): SettingsNavItem[] {
  return configs
    .filter(config => !config.visibleWhen || config.visibleWhen(ctx))
    .map(config => ({
      label: config.label,
      path: config.path,
      dataId: config.dataId,
      icon: config.icon,
      SVGIcon: config.SVGIcon,
      status: config.status,
      matchPaths: config.matchPaths,
      ...(config.children ? { children: resolveItems(config.children, ctx) } : {})
    }));
}

/** The resolved Billing rows, in the shape the sidebar draws. Hidden rows are dropped here. */
function itemsFromBillingTabs(tabs: BillingTabDescriptor[]): SettingsNavItem[] {
  return tabs
    .filter(tab => !tab.hidden)
    .map(tab => ({
      label: tab.label,
      path: tab.path,
      dataId: formatDataIdAttr(tab.label),
      icon: tab.icon,
      matchPaths: tab.matchPaths
    }));
}

export function useSettingsNavSections(): SettingsNavSection[] {
  const {
    isWorkspacesEnabled,
    isISVTeam,
    isWorkspaceTeam,
    isWebhookEventsEnabled,
    isH2OEnabled,
    getTeamSettings
  } = useTeamSettings();
  const { isOwnerOrHasPermission } = useHasPermission();
  /*
   * Resolved once when the settings shell mounts and held for as long as it stays mounted, so
   * the Billing rows are already right on the first settings page the user lands on and do not
   * re-decide themselves when they move between sections.
   */
  const billingNav = useBillingNav();

  const isTeamEditor = isOwnerOrHasPermission(RbacPermissions.TEAM_EDIT);
  const {
    isVpcPeeringEnabled: isVPCPeeringConnectionEnabled,
    isSnowflakePrivateLinkEnabled,
    isProxyAgentsEnabled
  } = useConnectivityAccess();
  const isEdgeAlertsEnabled = !!isH2OEnabled() && EnvConfig.isH20FeatureEnabled('ALERTS');
  const isAuditTablesEnabled = !!getTeamSettings()?.show_audit_feature;

  return useMemo(() => {
    const ctx: SettingsNavVisibilityContext = {
      isTeamEditor,
      isWorkspacesEnabled: !!isWorkspacesEnabled,
      isISVTeam: !!isISVTeam,
      isVPCPeeringConnectionEnabled,
      isSnowflakePrivateLinkEnabled,
      isProxyAgentsEnabled,
      isEdgeAlertsEnabled,
      isWebhookEventsEnabled: !!isWebhookEventsEnabled,
      isAuditTablesEnabled,
      isWorkspaceTeam: !!isWorkspaceTeam
    };

    return SETTINGS_SECTION_ORDER.map(id => {
      if (id === SettingsSection.BILLING) {
        return {
          id,
          title: SETTINGS_SECTION_TITLES[id],
          items: itemsFromBillingTabs(billingNav.tabs),
          loading: !billingNav.resolved || billingNav.retrying
        };
      }

      return {
        id,
        title: SETTINGS_SECTION_TITLES[id],
        items: resolveItems(SETTINGS_NAV[id] ?? [], ctx),
        loading: false
      };
    }).filter(section => section.loading || section.items.length > 0);
  }, [
    isTeamEditor,
    isWorkspacesEnabled,
    isISVTeam,
    isVPCPeeringConnectionEnabled,
    isSnowflakePrivateLinkEnabled,
    isProxyAgentsEnabled,
    isEdgeAlertsEnabled,
    isWebhookEventsEnabled,
    isAuditTablesEnabled,
    isWorkspaceTeam,
    billingNav
  ]);
}
